import { modal, ModalLayout, ModalSize, ModalTemplate } from '@ardoq/modal';
import styled from 'styled-components';
import { map } from 'rxjs/operators';
import { Stack } from '@ardoq/layout';
import { IntegrationJobPayload, IntegrationSchedule } from '@ardoq/api-types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { getScheduleDirectionId } from 'integrations/common/streams/schedules/utils';
import { text1, text2, text2Bold } from '@ardoq/typography';
import { IntegrationDate } from 'integrations/common/components/integrationDate/IntegrationDate';
import { Flex } from 'atomicComponents/Flex';
import { deleteSchedule } from 'integrations/common/streams/schedules/actions';
import { SchedulePolicy } from 'integrations/common/components/schedulePolicy/SchedulePolicy';
import { ScheduleStatus } from 'integrations/common/components/scheduleStatus/ScheduleStatus';
import { omit } from 'lodash';
import { t, Trans, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { type Locale } from '@ardoq/locale';
import { combineLatest } from 'rxjs';

const ScheduleText = styled.div`
  ${text1}
`;

const ScheduleField = styled.div`
  ${text2}
  width: 14ch;
`;

const ScheduleValue = styled.div`
  ${text2Bold}
`;

const Capitalized = styled.div`
  text-transform: capitalize;
`;

type DeleteScheduleModalProps = {
  onSubmit: VoidFunction;
  onCancel: VoidFunction;
  schedule: IntegrationSchedule;
  i18nLocale: I18nLocale;
  locale: Locale;
};

const DeleteScheduleModalComponent = ({
  onSubmit,
  onCancel,
  schedule,
  i18nLocale,
  locale,
}: DeleteScheduleModalProps) => {
  const handleSaveChanges = () => {
    dispatchAction(
      deleteSchedule(omit(schedule, 'meta') as IntegrationJobPayload)
    );
    onSubmit();
  };

  return (
    <ModalTemplate
      headerText={t(
        i18nLocale,
        'integrations:deleteScheduleModal.headerText',
        'Delete the schedule permanently?'
      )}
      dangerButtonText={t(
        i18nLocale,
        'integrations:deleteScheduleModal.dangerButtonText',
        'Delete schedule'
      )}
      secondaryButtonText={t(
        i18nLocale,
        'integrations:deleteScheduleModal.secondaryButtonText',
        'Keep schedule'
      )}
      modalSize={ModalSize.S}
      onDangerButtonClick={handleSaveChanges}
      onSecondaryButtonClick={onCancel}
    >
      <ModalLayout>
        <ScheduleText>
          <Trans
            i18nKey="integrations:deleteScheduleModal.scheduleWarning"
            defaults="If the schedule <bold>{{name}}</bold> is deleted it will stop immediately."
            values={{ name: schedule.jobOptions.name }}
            components={{ bold: <b>{''}</b> }}
          />
        </ScheduleText>
        <Stack gap="small">
          <Flex>
            <ScheduleField>
              {t(
                i18nLocale,
                'integrations:deleteScheduleModal.fieldName',
                'Name'
              )}
            </ScheduleField>
            <ScheduleValue>{schedule.jobOptions.name}</ScheduleValue>
          </Flex>
          <Flex>
            <ScheduleField>
              {t(
                i18nLocale,
                'integrations:deleteScheduleModal.fieldType',
                'Type'
              )}
            </ScheduleField>
            <ScheduleValue>
              <Capitalized>
                {getScheduleDirectionId(schedule.jobOptions.type) === 'import'
                  ? t(
                      i18nLocale,
                      'integrations:deleteScheduleModal.directionImport',
                      'import'
                    )
                  : t(
                      i18nLocale,
                      'integrations:deleteScheduleModal.directionExport',
                      'export'
                    )}
              </Capitalized>
            </ScheduleValue>
          </Flex>
          <Flex>
            <ScheduleField>
              {t(
                i18nLocale,
                'integrations:deleteScheduleModal.fieldInterval',
                'Interval'
              )}
            </ScheduleField>
            <ScheduleValue>
              <SchedulePolicy
                schedulingPolicy={schedule.schedulingPolicy}
                i18nLocale={i18nLocale}
              ></SchedulePolicy>
            </ScheduleValue>
          </Flex>
          <Flex>
            <ScheduleField>
              {t(
                i18nLocale,
                'integrations:deleteScheduleModal.fieldStatus',
                'Status'
              )}
            </ScheduleField>
            <ScheduleStatus
              meta={schedule.meta}
              i18nLocale={i18nLocale}
            ></ScheduleStatus>
          </Flex>
          <Flex>
            <ScheduleField>
              {t(
                i18nLocale,
                'integrations:deleteScheduleModal.fieldLastImported',
                'Last imported'
              )}
            </ScheduleField>
            <ScheduleValue>
              <IntegrationDate
                date={schedule.meta.lastCompleted}
                format="DATE"
                locale={locale}
                i18nLocale={i18nLocale}
              />
            </ScheduleValue>
          </Flex>
        </Stack>
        <ScheduleText>
          {t(
            i18nLocale,
            'integrations:deleteScheduleModal.unrecoverableNotice',
            'The schedule can’t be recovered once deleted.'
          )}
        </ScheduleText>
      </ModalLayout>
    </ModalTemplate>
  );
};

const viewModel$ = combineLatest([i18nLocale$, locale$]).pipe(
  map(([i18nLocale, locale]) => ({ i18nLocale, locale }))
);

const DeleteScheduleModal = connect(DeleteScheduleModalComponent, viewModel$);

export const startDeleteScheduleModal = (schedule: IntegrationSchedule) =>
  modal<boolean>(
    resolve => (
      <DeleteScheduleModal
        onSubmit={() => resolve(true)}
        onCancel={() => resolve(false)}
        schedule={schedule}
      />
    ),
    {
      autoFocus: true,
      shouldCloseOnEscapeKey: true,
      shouldCloseOnBackdropClick: false,
    }
  );
